/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/ban-types */
import { FirebaseError } from 'firebase-admin';
import {
  getAuth,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
  confirmPasswordReset,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updateEmail,
  checkActionCode,
  applyActionCode,
  onAuthStateChanged,
} from 'firebase/auth';
import { useState, useCallback, useContext } from 'react';
import useAuthWithEmail from './useAuthWithEmail';
import useGetHostName from './useGetHostName';
import sendEmail from './useSendEmail';
import AuthContext from '@/contexts/auth';
import SchoolContext from '@/contexts/school';
import { getResetPasswordVerificationMailLink } from '@/helpers';
import useAuth from '@/hooks/useAuth';
import firebase from '@/plugins/firebaseConfig';
class FirebaseSignInError implements FirebaseError {
  public code: string;
  public message: string;
  constructor({ code, message }: { code: string; message: string }) {
    this.code = code;
    this.message = message;
  }

  public toJSON(): any {
    return {
      code: this.code,
      message: this.message,
    };
  }
}

export const useFirebaseAuth = () => {
  const { setLoginError } = useContext(AuthContext);
  const { getLoginAttempt, loginSuccess, loginFailed } = useAuth();
  const { generateResetPasswordVerificationLink } = useAuthWithEmail();
  const { tenant_id, slug } = useContext(SchoolContext);
  const [hostname] = useGetHostName();
  const auth = getAuth(firebase);

  if (tenant_id) {
    auth.tenantId = tenant_id;
  }
  auth.languageCode = 'ja';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(false);

  const resetInput = useCallback(() => {
    setEmail('');
    setPassword('');
  }, []);

  const toggleMode = useCallback(() => {
    setIsLogin(!isLogin);
  }, [isLogin]);

  const loginUser = async (_email: string, _password: string): Promise<any> => {
    // Check login attempts
    const loginAttemps = await getLoginAttempt(_email);

    if (loginAttemps.email === null) {
      throw new FirebaseSignInError({
        code: 'auth/user-not-found',
        message: '',
      });
    }
    // Max time to next accept login (1 hour)
    const maxAttempts = 5;
    const currentTimeStamp = Math.floor(Date.now() / 1000);
    const timeToUnlock = currentTimeStamp - loginAttemps.login_attempt_time;
    if (timeToUnlock < 7200 && loginAttemps.login_attempts >= maxAttempts) {
      throw new FirebaseSignInError({
        code: 'auth/login-max-attempts',
        message: `アカウントロック中です。${Math.floor(
          (7200 - timeToUnlock) / 60,
        )}分以上経過したあとに再度ログインを行ってください。`,
      });
    }

    return new Promise((resolve, reject) => {
      signInWithEmailAndPassword(auth, _email.toLowerCase(), _password)
        .then(async (res) => {
          setEmail(_email);
          setPassword(_password);
          setLoginError(undefined);

          // Reset login attempt after login success
          const token = await res.user.getIdToken();
          await loginSuccess(token);

          resolve(res);
        })
        .catch(async (error) => {
          if (error.code != 'auth/multi-factor-auth-required') {
            // Increase login attempt
            await loginFailed(_email);
          }
          setLoginError(error);
          reject(error);
        });
    });
  };

  const reAuthentication = async (password: string) => {
    try {
      const credential = EmailAuthProvider.credential('', password);
      if (auth.currentUser) await reauthenticateWithCredential(auth.currentUser, credential);
      else {
        throw new FirebaseSignInError({
          code: 'unknowm',
          message: ``,
        });
      }
      return true;
    } catch (error) {
      setLoginError(error);
      return false;
    }
  };

  const verifyCheckPwReset = async (oobCode: string, tenantId: string) => {
    auth.tenantId = tenantId;
    const res = { isVerified: false, error: {} };
    await verifyPasswordResetCode(auth, oobCode)
      .then(() => {
        res.isVerified = true;
      })
      .catch((error) => {
        res.error = error;
      });
    return res;
  };

  const pwReset = async (oobCode: string, tenantId: string, newPassword: string) => {
    auth.tenantId = tenantId;
    const res = { isSuccess: false, error: {} };
    await verifyPasswordResetCode(auth, oobCode)
      .then(async () => {
        await confirmPasswordReset(auth, oobCode, newPassword)
          .then(async () => {
            res.isSuccess = true;
          })
          .catch((error) => {
            console.log(error);
            res.error = error;
          });
      })
      .catch((error) => {
        console.log(error);
        res.error = error;
      });

    return res;
  };

  const sendPwResetEmail = async (targetEmail: string, tenantId: string) => {
    const res = { isSuccess: false, error: {} };
    // 承認リンクを取得する
    try {
      const createData = {
        tenantId: tenantId,
        email: targetEmail,
      };
      const res2 = await generateResetPasswordVerificationLink(createData);
      // ユーザーに確認メールを送信する
      if (!res2.isSuccess) {
        throw new Error('検証メールリンクが生成できませんでした。');
      }

      const mailBody = getResetPasswordVerificationMailLink(
        targetEmail,
        `${hostname}/${slug}/password_reset?oobCode=${res2.data.oobCode}`,
      );

      // メールを送信する
      await sendEmail(mailBody)
        .then(() => {
          res.isSuccess = true;
        })
        .catch((error) => {
          console.log(error);
          res.error = error;
        });
      return res;
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const changeEmail = (_newEmail: string, _password: string) => {
    const res = { isSuccess: false, error: {} };
    auth.tenantId = tenant_id;

    return new Promise((resolve, reject) => {
      const user = auth.currentUser;
      if (user && user.email) {
        const credential = EmailAuthProvider.credential(user.email, _password);
        reauthenticateWithCredential(user, credential)
          .then(async () => {
            updateEmail(user, _newEmail)
              .then(() => {
                res.isSuccess = true;
                resolve(res);
              })
              .catch((error) => {
                res.error = error;
                reject(error);
              });
          })
          .catch((error) => {
            res.error = error;
            reject(error);
          });
      }
    });
  };

  const recoverEmail = async (oobCode: string, tenantId: string) => {
    auth.tenantId = tenantId;
    let restoredEmail: any = null;
    const res = { isSuccess: false, error: {} };

    await checkActionCode(auth, oobCode)
      .then(async (info) => {
        restoredEmail = info['data']['email'];
        await applyActionCode(auth, oobCode).then(async () => {
          res.isSuccess = true;
          await sendPasswordResetEmail(auth, restoredEmail, { url: `${hostname}/${slug}/login` })
            .then(() => {
              res.isSuccess = true;
            })
            .catch((error) => {
              res.error = error;
            });
        });
      })
      .catch((error) => {
        res.error = error;
      });

    return res;
  };

  const getIdToken = async () => {
    return new Promise((resolve, reject) => {
      auth.currentUser
        ?.getIdToken(true)
        .then((token) => {
          resolve(token);
        })
        .catch(() => {
          reject(null);
        });
    });
  };

  const getIdTokenExpireTimestamp = async () => {
    const idTokenResult = await auth.currentUser?.getIdTokenResult();
    return idTokenResult?.claims?.exp;
  };

  const getHasuraClaims = async (_user: any) => {
    const HASURA_TOKEN_KEY = 'https://hasura.io/jwt/claims';
    const idTokenResult = await _user.getIdTokenResult();
    const hasuraClaims: Object | undefined = idTokenResult.claims[HASURA_TOKEN_KEY];
    return hasuraClaims;
  };

  const checkCurrentUser = () => {
    return auth.currentUser;
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    resetInput,
    isLogin,
    toggleMode,
    loginUser,
    verifyCheckPwReset,
    pwReset,
    sendPwResetEmail,
    changeEmail,
    recoverEmail,
    getIdToken,
    getIdTokenExpireTimestamp,
    getHasuraClaims,
    checkCurrentUser,
    reAuthentication,
  };
};
